import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';
import { api, Overview as OverviewData } from '../lib/api';
import { formatNumber, formatPeriod } from '../lib/format';
import { Card, Empty, Loading, Stat, VolumeChart } from '../components/ui';

export function Overview() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = () =>
      api
        .get<OverviewData>('/api/overview')
        .then((result) => active && setData(result))
        .catch((caught) => active && setError(caught instanceof Error ? caught.message : String(caught)));

    load();
    // A wall-display refresh cadence: often enough to notice a problem, rare enough not to
    // hammer the database all day.
    const timer = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  if (error) return <div className="banner banner-danger">{error}</div>;
  if (!data) return <Loading label="Loading overview" />;

  const { stats, facilities, targets, unmappedCodes, volume, queue } = data;
  const delivered = (stats.byStatus.sent ?? 0) + (stats.byStatus.partial ?? 0);
  const failed = stats.byStatus.failed ?? 0;
  const needsMapping = stats.byStatus.unmapped ?? 0;
  const deliveryRate = stats.total > 0 ? Math.round((delivered / stats.total) * 100) : 0;
  const waiting = queue.inbound.pending + queue.inbound.running + queue.outbound.pending + queue.outbound.running;
  const abandoned = queue.inbound.dead + queue.outbound.dead;

  return (
    <div className="grid" style={{ gap: 20 }}>
      {targets.dryRun > 0 ? (
        <div className="banner banner-warn">
          <span>
            <strong>{targets.dryRun}</strong> enabled DHIS2 server
            {targets.dryRun === 1 ? ' is' : 's are'} in dry-run mode. Payloads are validated but
            nothing is saved in DHIS2. Turn dry run off on the <Link to="/targets">DHIS2 servers</Link>{' '}
            screen once a period looks right.
          </span>
        </div>
      ) : null}

      {abandoned > 0 ? (
        <div className="banner banner-danger">
          <span>
            <strong>{abandoned}</strong> queued job{abandoned === 1 ? ' has' : 's have'} been
            abandoned after repeated failures. Their data has not reached DHIS2 — open the{' '}
            <Link to="/queue">queue</Link> to see why and try again.
          </span>
        </div>
      ) : null}

      {facilities.missingOrgUnit > 0 ? (
        <div className="banner banner-danger">
          <span>
            <strong>{facilities.missingOrgUnit}</strong> facilit
            {facilities.missingOrgUnit === 1 ? 'y has' : 'ies have'} no DHIS2 organisation unit set.
            Their submissions will fail until one is assigned on the{' '}
            <Link to="/facilities">Facilities</Link> screen.
          </span>
        </div>
      ) : null}

      <div className="grid grid-stats">
        <Stat
          label="Delivered"
          value={formatNumber(delivered)}
          meta={`${deliveryRate}% of all submissions`}
          tint="var(--ok)"
        />
        <Stat
          label="Failed"
          value={formatNumber(failed)}
          meta={failed > 0 ? 'Needs attention' : 'Nothing failing'}
          tint={failed > 0 ? 'var(--danger)' : undefined}
        />
        <Stat
          label="Awaiting mapping"
          value={formatNumber(needsMapping)}
          meta={needsMapping > 0 ? 'Some values not sent' : 'All codes mapped'}
          tint={needsMapping > 0 ? 'var(--warn)' : undefined}
        />
        <Stat
          label="Facilities reporting"
          value={`${stats.facilitiesReporting}/${facilities.total}`}
          meta={`${facilities.enabled} enabled`}
          tint="var(--info)"
        />
        <Stat
          label="In the queue"
          value={formatNumber(waiting)}
          meta={
            waiting > 0
              ? `${queue.outbound.pending} waiting for DHIS2`
              : abandoned > 0
                ? `${abandoned} abandoned - see the queue`
                : 'Everything processed'
          }
          tint={abandoned > 0 ? 'var(--danger)' : waiting > 0 ? 'var(--info)' : undefined}
        />
        <Stat
          label="Last 24 hours"
          value={formatNumber(stats.last24h)}
          meta={stats.latestPeriod ? `Latest period ${formatPeriod(stats.latestPeriod)}` : 'No data yet'}
        />
      </div>

      <div className="grid grid-2">
        <Card title="Submissions, last 30 days">
          <VolumeChart data={volume} />
        </Card>

        <Card
          title="Codes with no mapping"
          actions={
            <Button as={Link} to="/mappings" kind="ghost" size="sm" renderIcon={ArrowRight}>
              Open mappings
            </Button>
          }
          bodyless
        >
          {unmappedCodes.length === 0 ? (
            <Empty title="Every code has a mapping">
              Nothing a facility has sent so far is being dropped.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Source code</th>
                    <th>Kind</th>
                    <th className="num">Reports affected</th>
                  </tr>
                </thead>
                <tbody>
                  {unmappedCodes.slice(0, 8).map((row) => (
                    <tr key={`${row.kind}:${row.sourceCode}`}>
                      <td className="mono">{row.sourceCode}</td>
                      <td className="muted" style={{ textTransform: 'capitalize' }}>
                        {row.kind}
                      </td>
                      <td className="num">{row.occurrences}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
